import React from 'react';
import { Lock } from 'lucide-react';

/**
 * "What happens after you upload" — the five live wizard stages as numbered
 * cards, ported from `Dashboard - Empty State.html`. Copy is Robbie
 * describing his own work, so the split of labour reads clearly before a
 * buyer commits a file. The footnote names the four stages that are locked
 * until an award exists, rather than showing them as cards nobody can enter.
 */

interface Stage {
  title: string;
  detail: string;
}

export interface JourneyPreviewProps {
  /**
   * Section heading. The default follows a drop zone, so it assumes an upload
   * is about to happen; reused above a working dashboard — where the reader
   * may never upload anything — it is overridden to describe instead.
   */
  heading?: string;
}

const STAGES: Stage[] = [
  { title: 'Upload BOM', detail: 'I read your files and pull out every part, quantity and spec.' },
  { title: 'Sourcing criteria', detail: 'You tell me what matters — price, quality, lead time, certifications.' },
  { title: 'Supplier discovery', detail: 'I search the marketplace and rank who can genuinely deliver.' },
  { title: 'Supplier outreach', detail: 'I write the inquiries and chase replies. You send with one click.' },
  { title: 'Quotes & award', detail: 'Quotes side by side, with my recommendation on who to award.' },
];

export const JourneyPreview: React.FC<JourneyPreviewProps> = ({ heading = 'What happens after you upload' }) => (
  <section className="rounded-card border border-line bg-surface px-[20px] py-s6">
    <div className="mb-[14px] flex flex-wrap items-baseline gap-s4">
      <span className="text-card-header text-ink">{heading}</span>
      <span className="text-meta text-ink-muted">Five stages · you approve each one · about 11 days end to end</span>
    </div>

    <div className="grid gap-s4 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
      {STAGES.map((stage, index) => (
        <div key={stage.title} className="rounded-[9px] border border-neutral-border bg-surface-hover px-[13px] py-[12px]">
          <div className="mb-[7px] flex items-center gap-[7px]">
            <span className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-round border border-action-border bg-action-bg font-mono text-[10.5px] font-bold text-action-deep">
              {index + 1}
            </span>
            <span className="text-[12.5px] font-semibold text-ink">{stage.title}</span>
          </div>
          <div className="text-meta leading-[1.5] text-ink-secondary">{stage.detail}</div>
        </div>
      ))}
    </div>

    <div className="mt-[12px] flex flex-wrap items-center gap-s3 border-t border-line-divider-soft pt-[12px]">
      <Lock className="h-[12px] w-[12px] shrink-0 text-ink-muted" />
      <span className="text-meta text-ink-muted">
        Financing, delivery, installation and contracting unlock after your first award.
      </span>
    </div>
  </section>
);

export default JourneyPreview;
